import { driver, type DriveStep, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import type { NavigateFunction } from 'react-router-dom'
import { getTourDefinition, type TourStepDef } from './tourRegistry'
import { writeTourSession, readTourSession } from './tourSession'
import { markTourComplete } from './tourStorage'
import type { TourId } from './tourIds'
import { matchesTourRoute, buildTourNavigatePath } from './tourNavigation'

let activeDriver: Driver | null = null

export function isDriverActive(): boolean {
  return activeDriver !== null
}

function waitForElement(selector: string, timeoutMs = 10000): Promise<Element | null> {
  return new Promise((resolve) => {
    const tryFind = () => document.querySelector(selector)
    const found = tryFind()
    if (found) {
      resolve(found)
      return
    }
    const observer = new MutationObserver(() => {
      const el = tryFind()
      if (el) {
        observer.disconnect()
        resolve(el)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    window.setTimeout(() => {
      observer.disconnect()
      resolve(tryFind())
    }, timeoutMs)
  })
}

function toDriveSteps(steps: TourStepDef[]): DriveStep[] {
  return steps.map((step) => ({
    element: step.element,
    popover: {
      title: step.popover.title,
      description: step.popover.description,
      side: step.popover.side,
      align: step.popover.align,
    },
  }))
}

function destroyActiveDriver(): void {
  if (activeDriver) {
    try {
      activeDriver.destroy()
    } catch {
      /* ignore */
    }
    activeDriver = null
  }
}

export function cancelGuidedTour(): void {
  destroyActiveDriver()
  writeTourSession(null)
}

export interface StartTourOptions {
  tourId: TourId
  navigate: NavigateFunction
  pathname: string
  search: string
  startIndex?: number
  onComplete?: () => void
}

async function ensureStepRoute(
  step: TourStepDef,
  navigate: NavigateFunction,
  pathname: string,
  search: string,
  tourId: TourId,
  stepIndex: number,
): Promise<boolean> {
  if (matchesTourRoute(step.route, pathname, search)) return true
  const path = buildTourNavigatePath(step.route)
  if (!path) return true
  writeTourSession({ tourId, stepIndex })
  navigate(path)
  return false
}

export async function startGuidedTour(options: StartTourOptions): Promise<void> {
  const { tourId, navigate, pathname, search, startIndex = 0, onComplete } = options
  const tour = getTourDefinition(tourId)
  const step = tour.steps[startIndex]
  if (!step) return

  const onRoute = await ensureStepRoute(step, navigate, pathname, search, tourId, startIndex)
  if (!onRoute) return

  destroyActiveDriver()

  if (step.element) {
    await waitForElement(step.element, 8000)
  }

  const driveSteps = toDriveSteps(tour.steps)
  let navigating = false

  const driverObj = driver({
    showProgress: true,
    progressText: '{{current}} of {{total}}',
    steps: driveSteps,
    onDestroyStarted: () => {
      if (!navigating) {
        writeTourSession(null)
      }
    },
    onDestroyed: () => {
      activeDriver = null
      if (!navigating) {
        onComplete?.()
      }
    },
    onNextClick: (_el, _step, { driver: d }) => {
      const nextIndex = (d.getActiveIndex() ?? 0) + 1
      const nextStep = tour.steps[nextIndex]
      if (!nextStep) {
        markTourComplete(tourId)
        writeTourSession(null)
        d.destroy()
        onComplete?.()
        return
      }
      const curPath = window.location.pathname
      const curSearch = window.location.search
      if (nextStep.route && !matchesTourRoute(nextStep.route, curPath, curSearch)) {
        navigating = true
        writeTourSession({ tourId, stepIndex: nextIndex })
        d.destroy()
        const path = buildTourNavigatePath(nextStep.route)
        if (path) navigate(path)
        navigating = false
        return
      }
      d.moveNext()
    },
    onPrevClick: (_el, _step, { driver: d }) => {
      d.movePrevious()
    },
  })

  activeDriver = driverObj
  writeTourSession({ tourId, stepIndex: startIndex })
  driverObj.drive(startIndex)
}

export async function resumeGuidedTourIfNeeded(
  navigate: NavigateFunction,
  pathname: string,
  search: string,
  onComplete?: () => void,
): Promise<void> {
  const session = readTourSession()
  if (!session) return
  const tour = getTourDefinition(session.tourId)
  const step = tour.steps[session.stepIndex]
  if (!step) {
    writeTourSession(null)
    return
  }
  if (!matchesTourRoute(step.route, pathname, search)) {
    const path = buildTourNavigatePath(step.route)
    if (path) navigate(path)
    return
  }
  await new Promise((r) => window.setTimeout(r, 350))
  await startGuidedTour({
    tourId: session.tourId,
    navigate,
    pathname,
    search,
    startIndex: session.stepIndex,
    onComplete,
  })
}
